import { DynamicPricingAdjustmentType } from "@/offering_v2/hooks/use_dynamic_rates_query";
import { WeekDay } from "../lib/datetime";
import { BookingType } from "./booking";

import { ImageVersion } from "./image";
import { AddOnType } from "./orders";
import { Space as FmSpaceData } from "../fm/data/space";

export interface Space {
  id: string;
  status: "active" | "inactive";
  spaceType: SpaceType;
  locationID: string;
  locationTimeZone: string;
  defaultLayoutID: string;
  name: string;
  inventoryCapacity: number;
  city: string;
  streetAddress: string;
  country: string;
  description: string;
  currencyCode: string | null;
  images: ImageVersion[];
  bookingHours: BookingHour[];
  bookingHour?: BookingHour;
  layouts: SpaceLayout[];
  pricings: Pricing[];
  priceRanges: PriceRanges;
  attributes: {
    floor?: string;
    withWindow?: boolean;
  };
  bookingPolicy: SpaceBookingPolicy;
  squareFootage: number | null;
  productType: ProductType | null;
  partnerID?: string;
  dynamicPriceRates?: DynamicPriceRate[];
  smartPricingEnabled: boolean;
}

export type LightSpace = Pick<
  Space,
  | "id"
  | "name"
  | "layouts"
  | "pricings"
  | "images"
  | "streetAddress"
  | "currencyCode"
  | "priceRanges"
  | "dynamicPriceRates"
  | "bookingHour"
>;

export interface ProductType {
  id: string;
  name: string;
  type: ProductTypeType;
  selectQuantity: boolean;
  invites: boolean;
  spaceTypeForAddOn?: string;
}

export type ProductTypeType =
  | "entire_location"
  | "private_office"
  | "meeting_room"
  | "access_add_on"
  | "add_on"
  | "event_space";

export interface SpecialOffering {
  spaceOfferingID: string;
  addOnType: AddOnType;
  bundled: boolean;
  addOnName: string;
  addOnDescription: string;
  addOnPrice: number;
  offerStartTime: string;
  offerEndTime: string;
  offerPeriodName: string;
}

export type SpaceType =
  | "entire_location"
  | "hotel_room"
  | "meeting_room"
  | "private_office"
  | "event_space";

export enum PricingType {
  Hourly = "hourly",
  Daily = "daily",
}

export interface Pricing {
  id: string;
  type: PricingType;
  price: number;
}

interface PriceRange {
  lowestPrice: number;
  highestPrice: number;
}

export interface DynamicPriceRate {
  bookingCriteria: {
    minBookingUnit?: number;
    bookingType: BookingType;
  }[];
  dynamicPriceFormula: {
    adjustmentType: DynamicPricingAdjustmentType;
    adjustmentValue: number;
  };
}

function toPriceRange(data: any): PriceRange {
  return {
    lowestPrice: data[0],
    highestPrice: data[1],
  };
}

export interface PriceRanges {
  daily?: PriceRange;
  hourly?: PriceRange;
}

export function toPriceRanges(data: any): PriceRanges {
  return {
    daily: data?.daily ? toPriceRange(data.daily) : undefined,
    hourly: data?.hourly ? toPriceRange(data.hourly) : undefined,
  };
}

export interface SpaceLayout {
  id: string;
  name: string;
  description: string;
  imageURL: string;
  capacity: number;
  spaceID: string;
  priceRanges: PriceRanges;
  pricings: Pricing[];
}

export interface BookingHour {
  dayOfWeek: WeekDay;
  openAt: string;
  closedAt: string;
}

export interface Amenity {
  id: string;
  name: string;
  iconURL: string;
}

export interface SpaceBookingPolicy {
  askForTax: boolean;
  requireTaxId: boolean;
  minDuration: number | null;
  cancellationHours: number | null;
  cancellationDays?: number | null;
  cancellationTime1DayBefore: string | null;
  disclaimer: string | null;
  checkoutWarning: string | null;
  marketingConsent: string | null;
  marketingConsentCheckedByDefault: boolean;
  terms: string | null;
  askForBookingReason: boolean | null;
  askForNumberOfGuests?: boolean | null;
  cancellationWarning?: string;
}

export function getMaxCapacity(space: { layouts: { capacity: number }[] }) {
  let maxCapacity = 0;

  space.layouts.forEach((layout) => {
    if (layout.capacity > maxCapacity) {
      maxCapacity = layout.capacity;
    }
  });

  return maxCapacity;
}

export function getMaxCapacityLabel(space: Space | FmSpaceData) {
  const maxCapacity = getMaxCapacity(space);

  if (maxCapacity <= 1) {
    return "";
  }

  return `${maxCapacity} Person (Max)`;
}

export const SHOW_SPACE_NAME_IN_PRODUCT_SPACE_TYPES: SpaceType[] = [
  "event_space",
  "meeting_room",
];
